import React, { useRef, useState, useEffect } from "react";
import { Modal, Box, Typography, Button } from "@mui/material";
import { css } from "aphrodite";
import styles from "./styles";
import { useTranslation } from "react-i18next";
import { RequestLayout } from "../RequestLayout";
import { secondsToString } from "../../../../Utils";

const OtpModal = ({
    visible,
    validateButtonText = "Validate OTP",
    validateButtonColor = "primary",
    phoneOrEmail = "+30 69XXXXXXXX",
    isLoading = false,
    error = "",
    timer = 30,
    onClose = () => {},
    onValidateOtp = (_otpFinalValue) => {},
    handleResendOtp = () => {},
    handleReturnToHome = () => {},
}) => {
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const inputsRef = useRef([]);
    const [t] = useTranslation(["translation"]);
    const [timeLeft, setTimeLeft] = useState(timer);
    const [canResend, setCanResend] = useState(false);
    const [isLockedOut, setIsLockedOut] = useState(false);

    useEffect(() => {
        if (error?.code === 13) {
            setTimeLeft(120);
            setCanResend(false);
            setIsLockedOut(true);
        }
    }, [error?.code]);

    useEffect(() => {
        let interval;
        if (!canResend && visible && timeLeft > 0) {
            interval = setInterval(() => {
                setTimeLeft((prev) => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        setCanResend(true);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [canResend, visible, timeLeft]);

    useEffect(() => {
        if (visible) {
            setCanResend(false);
            setTimeLeft(timer);
            setIsLockedOut(false);
            setOtp(["", "", "", "", "", ""]);
            setTimeout(() => {
                inputsRef.current[0]?.focus();
            }, 100);
        }
    }, [visible, timer]);

    const handleChange = (index, value) => {
        if (!/^\d?$/.test(value) || isLockedOut) return;
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        if (value && index < 5) {
            inputsRef.current[index + 1].focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (isLockedOut) return;
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputsRef.current[index - 1].focus();
        }
        if (e.key === "Enter") {
            handleValidate();
        }
    };

    const handleValidate = () => {
        const finalCode = otp.join("");
        if (finalCode.length === 6 && !isLockedOut) {
            onValidateOtp(finalCode);
        }
    };

    const handleResend = () => {
        if (canResend) {
            setOtp(["", "", "", "", "", ""]);
            setIsLockedOut(false);
            handleResendOtp();
            setCanResend(false);
            setTimeLeft(timer);
        }
    };

    return (
        <Modal open={visible} onClose={onClose}>
            <Box>
                <RequestLayout loading={isLoading} overlayLoading>
                    <Box className={css(styles.modal)}>
                        <Typography variant="h6" className={css(styles.heading)}>
                            {t("Please Enter the OTP for Verification.")}
                        </Typography>
                        <Typography variant="body2" className={css(styles.subtext)}>
                            {t("An OTP (One Time Password) has been sent to ")} {phoneOrEmail}
                        </Typography>

                        <Box className={css(styles.otpBox)}>
                            {otp.map((digit, idx) => (
                                <input
                                    key={idx}
                                    maxLength={1}
                                    value={digit}
                                    ref={(el) => (inputsRef.current[idx] = el)}
                                    onChange={(e) => handleChange(idx, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(e, idx)}
                                    className={css(styles.otpInput)}
                                    type="text"
                                    inputMode="numeric"
                                    disabled={isLockedOut}
                                />
                            ))}
                        </Box>

                        {isLockedOut && (
                            <Typography 
                                variant="body2" 
                                sx={{ color: "red", my: 2, textAlign: "center", fontWeight: 'bold' }}
                            >
                                {t("The OTP has been invalidated due to multiple failed attempts. Please wait for the resend option to become available.")}
                            </Typography>
                        )}

                        <Typography
                            variant="body2"
                            className={css(styles.resend)}
                            onClick={() => handleResend()}
                            style={{
                                opacity: canResend ? 1 : 0.4,
                                cursor: canResend ? "pointer" : "default",
                                marginBottom: '20px'
                            }}
                        >
                            {canResend
                                ? t("Resend OTP")
                                : `${t("Resend again in")} ${secondsToString(timeLeft)}`}
                        </Typography>

                        <Button
                            disableElevation
                            variant="contained"
                            color={validateButtonColor}
                            onClick={handleValidate}
                            className={css(styles.validateButton)}
                            disabled={otp.join("").length !== 6 || isLockedOut || canResend}
                        >
                            {validateButtonText}
                        </Button>

                        <Typography
                            variant="body2"
                            className={css(styles.backHome)}
                            onClick={onClose}
                            style={{ cursor: 'pointer', marginTop: '15px' }}
                        >
                            {t("Back")}
                        </Typography>
                    </Box>
                </RequestLayout>
            </Box>
        </Modal>
    );
};

export default OtpModal;